/* eslint-disable react/prop-types */
import {lazy, Suspense, useCallback, useContext, useEffect, useMemo, useState} from 'react'

import cookie from '@s-ui/js/lib/cookie'
import {useSuiContext} from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {useMount} from '@s-ui/react-hooks'
import ScriptJsonLD from '@s-ui/react-script-json-ld'
import {SaitamaAd} from '@adv-mt/advertising-publift'
import AdDisplay from '@adv-mt/mt-ad-display'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import {
  FORM_PRESENTATIONS,
  FormContactSellerV2 as ContactCardV2,
  StickyContactBar
} from '@adv-mt/mt-form-contact-seller'
import Layout, {layoutBasicMaxContentWidths} from '@adv-mt/mt-layout-basic'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import {listRelatedAdsContactTypes} from '@adv-mt/mt-list-related-ads'
import NetDrawer, {useReopenDrawerOnSession} from '@adv-mt/mt-net-drawer'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {ToastList, ToastProvider, ToastViewport} from '@adv-mt/ui/toast'

import LinkFactory from '../../../../components/LinkFactory/index.js'
import CsatNpsTracker from '../../../../helpers/csatNps/CsatNpsTracker.js'
import {adaptDomainNewsFeed} from '../../../News/List/adapters/adaptDomainNewsFeed.js'
import SecondaryBar from './components/secondaryBar/SecondaryBar.js'
import Separator from './components/separator/Separator.js'
import {DRAWER_TYPES, resolveContactFormDrawer} from './drawers/config.js'
import DetailDrawer from './drawers/index.js'
import getVisibleAdDetailSectionIds from './hooks/getVisibleAdDetailSectionIds.js'
import useContactCardProps from './hooks/useContactCardProps.js'
import useContactHandlers from './hooks/useContactHandlers.js'
import useFavoriteLead from './hooks/useFavoriteLead.js'
import useFavorites from './hooks/useFavorites.js'
import useLeadTracking from './hooks/useLeadTracking.js'
import usePostContactModal, {POST_CONTACT_MODALS} from './hooks/usePostContactModal.js'
import useRelatedAdsModalTracking from './hooks/useRelatedAdsModalTracking.js'
import useReservationHash from './hooks/useReservationHash.js'
import getAdvantagesVisibility from './panels/advantages/getAdvantagesVisibility.js'
import Advantages from './panels/advantages/index.js'
import CharacteristicsPanel from './panels/characteristics/index.js'
import DescriptionPanel from './panels/description/index.js'
import FinancingPanel from './panels/financing/index.js'
import {resolveFinancingMode} from './panels/financing/utils.js'
import FinancingConditions from './panels/financingConditions/index.js'
import GalleryPanel from './panels/gallery/index.js'
import NavigationFooter from './panels/navigationFooter/index.js'
import NetAssistant from './panels/netAssistant/index.js'
import News from './panels/news/index.js'
import PriceInfo from './panels/priceInfo/index.js'
import RelatedAds from './panels/relatedAds/index.js'
import RentingServices from './panels/rentingServices/index.js'
import ReportAd from './panels/reportAd/index.js'
import Reservation, {RESERVATION_PLACEMENTS} from './panels/reservation/index.js'
import Seller from './panels/seller/index.js'
import Trust from './panels/trust/index.js'
import {BASE_CLASS} from './config.js'
import {useNotify} from './notifications.js'
import {computeGalleryVideo, getContactOfferTypeFlags} from './utils.js'

import './styles/index.scss'

// A `mt-modal-basic` modal, not a Base UI drawer, so it cannot join
// `DRAWER_RENDERERS` and gets its own mount below. It is rendered only once open
// because it fires its `open` tracking event on mount, not on `open` flipping.
const LazyModalLastCall = lazy(() => import(/* webpackChunkName: "LazyModalLastCall" */ '@adv-mt/mt-modal-last-call'))
const LazyModalRelatedAds = lazy(() =>
  import(/* webpackChunkName: "LazyModalRelatedAds" */ '@adv-mt/mt-modal-related-ads')
)
const LazyModalChangeUserPassword = lazy(() =>
  import(/* webpackChunkName: "LazyModalChangeUserPassword" */ '@adv-mt/mt-modal-change-user-password')
)

const DetailNewContent = ({
  ad = {},
  adFavoriteInfo,
  adRentingForBusiness,
  adType = {},
  alertSearch,
  breadcrumb = [],
  dealerAds = {},
  dealerFavoriteInfo,
  dealerReviews = [],
  extendedTrackingData = {},
  financingData,
  groupedSpecifications,
  helmet = {},
  leadData,
  multiProvinceTrackingData,
  newsFeed,
  pageErrors = {},
  pageName,
  relatedAds = [],
  reservationNewFlow = {},
  showB2cFinancingPanel = false,
  showC2cFinancing,
  tagclouds = [],
  targetBuyer,
  vehicleCertification = {},
  vehicleHistory,
  vehicleInfo = {},
  seller = {},
  isPreview,
  pageTrackingData = {},
  __HTTP__ = {}
}) => {
  // Even though this is placed at the top of the page,
  // When we do Client-side rendering the component is rendered
  const {redirectTo} = __HTTP__

  const {domain, i18n} = useSuiContext()
  const {user, isLogged} = useContext(AuthenticationContext)
  const {page: analyticsTrackPage, track: analyticsTrack} = useAnalyticsTrack()

  const [activeDrawer, setActiveDrawer] = useState(null)
  // The renting selection lives here, not inside PriceInfo, because three surfaces read it: the
  // sticky bar's price, the contact form's target buyer options, and the lead payload.
  const [rentingSelection, setRentingSelection] = useState({monthlyFee: ad.monthlySubscriptionFee})
  const [activeTargetBuyer, setActiveTargetBuyer] = useState(targetBuyer)
  const rentingMonthlyFee = rentingSelection.monthlyFee
  const [appliedFinancingData, setAppliedFinancingData] = useState(null)
  const [isNetOpen, setIsNetOpen] = useState(false)
  const [netSearchValue, setNetSearchValue] = useState(null)
  const [netInitialSuggestions, setNetInitialSuggestions] = useState(null)

  const notify = useNotify()
  const {reservationDrawerContentType} = useReservationHash({adId: ad.id, notify})
  const {isFavorite, isDealerFavorite} = useFavorites({
    adId: ad.id,
    dealerId: seller.dealer?.id,
    initialIsFavorite: Boolean(adFavoriteInfo),
    initialIsDealerFavorite: Boolean(dealerFavoriteInfo)
  })

  // v2 emits no analytics of its own: the card hands every action up and this
  // hook owns the `Lead Triggered` payload, so the event keeps the legacy schema.
  const sendMessageTrackingAd = useMemo(
    () => ({
      id: ad.id,
      price: ad.price,
      vehicleHistory: Boolean(vehicleHistory?.hasReport),
      ...(seller.dealer?.pack && {pack: {type: seller.dealer.pack}}),
      ...(ad.priceDrop?.originalPrice > ad.price && {
        priceDropData: {amount: ad.priceDrop.originalPrice - ad.price}
      })
    }),
    [ad.id, ad.price, ad.priceDrop, vehicleHistory, seller.dealer?.pack]
  )
  const {
    onContact: onLeadContact,
    trackAlertCreated,
    trackLeadError,
    trackSendMessageClick
  } = useLeadTracking({adId: ad.id, leadData, seller, trackingAd: sendMessageTrackingAd})

  const {contactType, contactUserData, onClosePostContactModal, openModal, triggerPostContactModal} =
    usePostContactModal({
      adId: ad.id,
      dealerId: seller.dealer?.id,
      hasRelatedAds: relatedAds.length > 0,
      makeId: ad.makeId,
      // The real offer type id, never `isVOorVNorKM0`: that boolean is also true for
      // VO, which the use case does not accept, and passing it fails silently.
      offerTypeId: adType.offerTypeId
    })

  const {onContactRelatedAd, onToggleRelatedAdsModal} = useRelatedAdsModalTracking({
    adDetail: {ad, adType, seller, vehicleInfo},
    contactType
  })

  const config = domain.get('config')
  const {DEALER_PACKS} = config.get('dealer')
  const {PubTop1, PubInline1Desktop, ADS_LIST_POSITIONS} = config.get('advertising')
  const formContactSellerId = config.get('FORM_CONTACT_SELLER_ID')
  const searchConfig = config.get('search')
  const {OFFER_TYPE_SUBSCRIPTION} = searchConfig
  const {RESERVATION_STATUSES} = config.get('financing')
  const {DRAWER_REOPEN_SESSION_KEY, NET_SUGGESTION_TYPE} = config.get('COMMON').AI_ASSISTANT
  const {AD_DETAIL_PAGE_SECTIONS_ORDER, AD_DETAIL_PAGE_SECTION_IDS} = config.get('COMMON').AD_DETAIL_PAGE_SECTIONS
  const {
    COOKIES: {VISITED_AD: visitedAdCookieKey},
    RECOMMENDED_ADS: {
      adsList: {visitedAdSessionStorageKey}
    }
  } = config.get('COMMON')
  const {HIT_ORIGINS, LEAD_ERROR_HIT_INFORMATIONS, LEAD_TYPES, TRACKING_EVENT_NAMES} = config.get('tracking')

  const isCoches = config.get('APP_NAME') === config.get('APP_NAME_COCHES')
  const isMobile = config.get('isMobile')

  // MTR-120441: same casuistics as legacy's NavigationAdToolbar (coches-only, PRO-dealer,
  // logged-in) — replicated here so the heart in the new detail's toolbar shows the same
  // post-favorite lead popover and sends the same lead. Toolbar only, same as legacy.
  const isFavoriteLeadEnabled = Boolean(seller?.isDealer) && isLogged && isCoches

  const isHigherThanTabletResolution = useMediaQuery(useMediaQueryBreakpoints.xl, {defaultMatches: !isMobile})

  const isDealerSubscription = Boolean(seller.isDealer) && seller.dealer?.pack === DEALER_PACKS.SUBSCRIPTION

  /**
   *  Advertising banners (mid screen and gallery modal) are only shown for:
   *  - Private sellers
   *  - START dealers without the branding addon
   *  Every other pack is left out on purpose, so a new pack never gets banners by default.
   */
  const DEALER_PACKS_WITH_ADVERTISING = [DEALER_PACKS.START]
  const dealerPack = seller.dealer?.pack
  const hasBrandingAddon = Boolean(seller.dealer?.brandingActive)
  const hasAdvertising =
    !hasBrandingAddon &&
    (Boolean(seller.isPrivate) || (Boolean(dealerPack) && DEALER_PACKS_WITH_ADVERTISING.includes(dealerPack)))

  const handleShowAllRatings = async () => {
    setActiveDrawer({
      data: {ad, pageName, ratings: seller.dealer?.ratings, reviews: dealerReviews},
      type: DRAWER_TYPES.DEALER_RATINGS
    })

    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      event: TRACKING_EVENT_NAMES.RATINGS_CLICKED,
      origin: HIT_ORIGINS.RATING_DETAIL_CONTACT_FORM
    })

    analyticsTrack(eventData)
  }
  // MTR-120000: product reversed the earlier per-seller split - every seller,
  // private included, now opens the same drawer. This is AHEAD of Figma, not
  // synced with it: the live Case=Particular symbols (15022:12986 desktop,
  // 15462:47611 mobile) are still 442px/97px, the tall inline-form card - re-
  // read 2026-09-28, unchanged since figma-connect/contact-card.figma.tsx was
  // written. That binding's `Case: Particular -> INLINE` mapping is therefore
  // still CORRECT for the current design; do not "fix" it to DRAWER without a
  // design update, or Code Connect would describe a card Figma does not show.
  const formPresentation = FORM_PRESENTATIONS.DRAWER
  // Gates the whole contact-drawer wiring below: INLINE renders no FormDrawer at
  // all, so there is nothing for `activeDrawer` to own and the sticky bar must keep
  // its scroll fallback instead.
  const isFormInDrawer = formPresentation === FORM_PRESENTATIONS.DRAWER
  const isContactFormOpen = isFormInDrawer && activeDrawer?.type === DRAWER_TYPES.CONTACT_FORM
  const isRenting = adType.offerTypeId === OFFER_TYPE_SUBSCRIPTION
  // Same condition PriceInfo uses to show its tabs: the form's buttons and those tabs are two
  // controls over one state, so one of them appearing without the other would be a lie.
  const showTargetBuyerOptions = isRenting && Boolean(adRentingForBusiness)
  // Same shape both legacy send sites build, so a subscription lead carries
  // `preferences.subscription.commitment`. Omitting it drops the whole block from the lead.
  const {commitment: selectedRentingCommitment} = rentingSelection
  // Memoized (not a plain derivation) so handleFavoriteLeadConfirm's useCallback below gets a
  // stable reference instead of a new object identity on every render.
  const subscriptionCommitment = useMemo(
    () =>
      isRenting && selectedRentingCommitment
        ? {
            totalMonths: selectedRentingCommitment.totalMonths,
            monthlyPrice: selectedRentingCommitment.monthlyPrice,
            kmIncluded: selectedRentingCommitment.kmIncluded,
            kmIncludedPeriod: selectedRentingCommitment.kmIncludedPeriod,
            targetBuyer: activeTargetBuyer
          }
        : undefined,
    [isRenting, selectedRentingCommitment, activeTargetBuyer]
  )

  const {handleFavoriteLeadConfirm} = useFavoriteLead({
    ad,
    analyticsTrack,
    domain,
    i18n,
    leadData,
    leadTypes: LEAD_TYPES,
    notify,
    pageName,
    seller,
    subscriptionCommitment,
    user
  })

  const {isNewVehicle, isVOorVNorKM0} = getContactOfferTypeFlags({adType, searchConfig})
  const [buyerIntentions, setBuyerIntentions] = useState([])
  const [presetBuyerIntentions, setPresetBuyerIntentions] = useState(undefined)
  const [presetMessage, setPresetMessage] = useState(undefined)

  useEffect(() => {
    if (!isVOorVNorKM0) return

    domain
      .get('get_buyer_intentions_user_use_case')
      .execute()
      .then(([error, response]) => {
        if (!error && response?.buyerIntentions) setBuyerIntentions(response.buyerIntentions)
      })
  }, [domain, isVOorVNorKM0])

  const handleFinancingContactSellerCtaClick = useCallback(
    ({entry, fee, terms} = {}) => {
      setPresetBuyerIntentions(['FINANCING'])
      if (fee == null || terms == null) return
      setPresetMessage(
        entry
          ? i18n.t('CONTACT_SELLER_V2.FORM.FINANCING_PRESET_MESSAGE_WITH_ENTRY', {
              entry: i18n.c(entry),
              fee: i18n.c(fee),
              terms
            })
          : i18n.t('CONTACT_SELLER_V2.FORM.FINANCING_PRESET_MESSAGE', {fee: i18n.c(fee), terms})
      )
    },
    [i18n]
  )
  const computedVideo = useMemo(() => computeGalleryVideo({ad, isCoches}), [ad, isCoches])
  const showNetAssistant = !isDealerSubscription && !isPreview

  const showReservation =
    seller?.isDealer &&
    seller?.dealer?.hasReservation &&
    reservationNewFlow.adReservationStatus !== RESERVATION_STATUSES.NON_RESERVABLE
  const showPriceSeparator = !isDealerSubscription && (!showReservation || isHigherThanTabletResolution)

  // Once the panel settles a quote it owns every figure the price block reads, so we swap the whole
  // object like legacy does. Merging field by field left everything but the fee at its server value.
  const priceInfoFinancingData = appliedFinancingData ?? financingData

  // Same module the Advantages panel uses, so the nav anchor exists iff a card renders
  const {showAdvantages} = getAdvantagesVisibility({ad, adType, vehicleHistory, config})

  const handleContactFormOpenChange = useCallback(isOpen => {
    setActiveDrawer(current => resolveContactFormDrawer(current, isOpen))

    if (isOpen) {
      setIsNetOpen(false)
    }
  }, [])

  // The sticky bar's only drawer request is the contact form, so it funnels through
  // the same handler as the card's trigger rather than writing `activeDrawer`
  // directly. Its `{type, data}` payload is dropped exactly as before: the type has
  // no renderer, so nothing ever read the data.
  const handleStickyBarOpenDrawer = useCallback(() => handleContactFormOpenChange(true), [handleContactFormOpenChange])

  const contactHandlers = useContactHandlers({
    leadErrorHitInformations: LEAD_ERROR_HIT_INFORMATIONS,
    notify,
    onLeadContact,
    trackLeadError,
    triggerPostContactModal
  })
  const {autoGeneratedPassword, clearAutoGeneratedPassword} = contactHandlers

  const {
    cardProps: contactCardProps,
    galleryProps: contactGalleryProps,
    stickyProps: stickyContactBarProps
  } = useContactCardProps({
    ad,
    alertSearch,
    buyerIntentions,
    config,
    formPresentation,
    handlers: contactHandlers,
    isContactFormOpen,
    isFormInDrawer,
    isMobile,
    isNewVehicle,
    isRenting,
    isVOorVNorKM0,
    leadData,
    multiProvinceTrackingData,
    onContactFormOpenChange: handleContactFormOpenChange,
    onOpenDrawer: setActiveDrawer,
    onShowAllRatings: handleShowAllRatings,
    onStickyBarOpenDrawer: handleStickyBarOpenDrawer,
    onTargetBuyerChange: setActiveTargetBuyer,
    pageName,
    presetBuyerIntentions,
    presetMessage,
    seller,
    showTargetBuyerOptions,
    subscriptionCommitment,
    targetBuyer: activeTargetBuyer,
    trackAlertCreated,
    trackSendMessageClick,
    user
  })

  const newsFeedAdapted = useMemo(
    () => (newsFeed ? adaptDomainNewsFeed({domainConfig: config, newsFeed}) : newsFeed),
    [config, newsFeed]
  )

  const visibleSectionIds = useMemo(
    () =>
      getVisibleAdDetailSectionIds({
        sectionsOrder: AD_DETAIL_PAGE_SECTIONS_ORDER,
        sectionIds: AD_DETAIL_PAGE_SECTION_IDS,
        isDealerSubscription,
        showAdvantages,
        hasDescription: Boolean(ad.description),
        hasFinancing: Boolean(
          resolveFinancingMode({
            dealerPacks: DEALER_PACKS,
            financingData,
            seller,
            showB2cFinancingPanel,
            showC2cFinancing
          })
        )
      }),
    [
      AD_DETAIL_PAGE_SECTIONS_ORDER,
      AD_DETAIL_PAGE_SECTION_IDS,
      isDealerSubscription,
      showAdvantages,
      ad.description,
      DEALER_PACKS,
      seller,
      financingData,
      showB2cFinancingPanel,
      showC2cFinancing
    ]
  )

  const adInfo = useMemo(() => {
    if (isRenting) {
      return {
        title: ad.title,
        price: rentingMonthlyFee != null ? `${i18n.c(rentingMonthlyFee)}${i18n.t('COMMON.PERIOD.MONTHLY')}` : undefined
      }
    }
    return {title: ad.title, price: ad.price != null ? i18n.c(ad.price) : undefined}
  }, [ad.title, ad.price, i18n, isRenting, rentingMonthlyFee])

  useMount(() => {
    if (redirectTo || isPreview) return
    analyticsTrackPage(pageTrackingData)
    analyticsTrack({event: TRACKING_EVENT_NAMES.DETAIL_REVAMP_TEST_VIEWED})
  })

  useMount(() => {
    if (redirectTo || isPreview) return
    domain.get('save_page_view_hit_tracking_use_case').execute({adId: ad.id})
  })

  // The listing reads both to show the visited ad recommender on the way back: the cookie once
  // after this visit, the session storage for the whole session on few-results pages.
  useMount(() => {
    sessionStorage.setItem(visitedAdSessionStorageKey, ad.id)
    cookie.set(visitedAdCookieKey, ad.id)
  })

  /**
   * The only writer of `isNetOpen`, so the drawer-exclusion invariant holds on all three open
   * paths — the assistant panel, the drawer's own onOpen, and the post-login session
   * reopen below, which restores through bfcache with React state (and therefore a
   * possibly open contact drawer) intact.
   *
   * One-directional on purpose: it clears `activeDrawer` but never reads `isNetOpen`,
   * and the contact side never reads `isNetOpen` either. A useEffect reconciling the
   * two slots could oscillate against the session reopen; two handlers that only
   * write cannot. Clearing goes through `resolveContactFormDrawer` so a Carfax or
   * technical-details drawer is left alone.
   */
  const setNetDrawerOpen = useCallback(isOpen => {
    if (isOpen) {
      setActiveDrawer(current => resolveContactFormDrawer(current, false))
    }

    setIsNetOpen(isOpen)
  }, [])

  useReopenDrawerOnSession({
    isEnabled: !isDealerSubscription,
    storageKey: DRAWER_REOPEN_SESSION_KEY,
    onToggleDrawer: setNetDrawerOpen
  })

  const onOpenNet = useCallback(
    searchValue => {
      const {suggestions, ...search} = searchValue || {}

      setNetSearchValue(search?.text ? search : null)
      setNetInitialSuggestions(suggestions || null)
      setNetDrawerOpen(true)
    },
    [setNetDrawerOpen]
  )

  const onCloseNet = useCallback(() => {
    setNetSearchValue(null)
    setNetInitialSuggestions(null)
    setNetDrawerOpen(false)
  }, [setNetDrawerOpen])

  const onCloseDrawer = useCallback(() => setActiveDrawer(null), [])

  return (
    <>
      <CsatNpsTracker adId={ad.id} isDisabled={isPreview || !ad.id || Boolean(redirectTo)} />
      <AdDisplay pageName={pageName} ad={ad} adType={adType} seller={seller} />
      {isHigherThanTabletResolution && (
        <div className={`${BASE_CLASS}-topBanner`}>
          <SaitamaAd id={ADS_LIST_POSITIONS[PubTop1].id} />
        </div>
      )}
      <Layout.FullWidth
        linkFactory={LinkFactory}
        isHeaderFlush
        className={`${BASE_CLASS}-layout`}
        maxContentWidth={layoutBasicMaxContentWidths.LARGE}
      >
        <SecondaryBar
          ad={ad}
          adInfo={adInfo}
          breadcrumb={breadcrumb}
          contractId={seller.contractId}
          extendedTrackingData={extendedTrackingData}
          hasDiscardButton={!isRenting}
          isFavorite={isFavorite}
          isFavoriteLeadEnabled={isFavoriteLeadEnabled}
          onFavoriteLeadConfirm={handleFavoriteLeadConfirm}
          visibleSectionIds={visibleSectionIds}
        />
        <main className={BASE_CLASS}>
          <GalleryPanel
            ad={ad}
            adType={adType}
            video={computedVideo}
            breadcrumb={breadcrumb}
            contractId={seller.contractId}
            extendedTrackingData={extendedTrackingData}
            hasDiscardButton={!isRenting}
            isFavorite={isFavorite}
            isProfessionalDealer={Boolean(seller.isDealer)}
            hidePhone={contactGalleryProps.hidePhone}
            phone={contactGalleryProps.phone}
            seller={seller}
            showGalleryAd={hasAdvertising && isHigherThanTabletResolution}
          />
          <div className={`${BASE_CLASS}-columns`}>
            {/*
              Single mount, in the aside slot that already exists at $w-detail-column-aside.
              First in the DOM so keyboard and screen-reader order match the visual order at
              xl, where the grid paints this beside PriceInfo; `grid-column: 2` in
              styles/index.scss pins it to the 416px track, since auto-placement would
              otherwise give it the 798px main one. The id lives here and never on the form
              component.

              ⚠️ `-contact` is `display: none` below xl (see `&-contact` in styles/index.scss), so
              this card is DESKTOP-ONLY at runtime; below xl the contact surface is the
              StickyContactBar below.

              The `complementary` landmark needs its own accessible name: none of the three v2
              contracts exposes a prop for one, so it is set here instead.
            */}
            <aside
              aria-label={i18n.t('FORM.CONTACT_SELLER_LABEL')}
              className={`${BASE_CLASS}-contact`}
              id={formContactSellerId}
            >
              <ContactCardV2 {...contactCardProps} />
              {/* Reservation aside - Desktop */}
              {showReservation && (
                <Reservation
                  ad={ad}
                  financingData={financingData}
                  openDrawerContentType={isHigherThanTabletResolution ? reservationDrawerContentType : null}
                  placement={RESERVATION_PLACEMENTS.ASIDE}
                  reservationNewFlow={reservationNewFlow}
                  seller={seller}
                />
              )}
            </aside>
            <div className={`${BASE_CLASS}-paddedContent`}>
              <PriceInfo
                ad={ad}
                adRentingForBusiness={adRentingForBusiness}
                extendedTrackingData={extendedTrackingData}
                financingData={priceInfoFinancingData}
                isFavorite={isFavorite}
                isPersonalLoanAvailable={showC2cFinancing}
                optionalEquipment={vehicleInfo.equipment?.optional}
                targetBuyer={activeTargetBuyer}
                onOpenDrawer={setActiveDrawer}
                onSelectionChange={setRentingSelection}
                onTargetBuyerChange={setActiveTargetBuyer}
              />

              {/* Reservation inline - Mobile & Tablet */}
              {showReservation && (
                <Reservation
                  ad={ad}
                  financingData={financingData}
                  openDrawerContentType={isHigherThanTabletResolution ? null : reservationDrawerContentType}
                  placement={RESERVATION_PLACEMENTS.INLINE}
                  reservationNewFlow={reservationNewFlow}
                  seller={seller}
                />
              )}

              {showPriceSeparator && <Separator />}

              {isDealerSubscription && <RentingServices onOpenDrawer={setActiveDrawer} seller={seller} />}
              <CharacteristicsPanel
                ad={ad}
                groupedSpecifications={groupedSpecifications}
                onOpenDrawer={setActiveDrawer}
                optionalEquipment={vehicleInfo.equipment?.optional}
                vehicleInfo={vehicleInfo}
              />

              <DescriptionPanel description={ad.description} />

              {hasAdvertising && (
                <div className={`${BASE_CLASS}-midBannerWrapper`}>
                  <SaitamaAd id={ADS_LIST_POSITIONS[PubInline1Desktop].id} />
                </div>
              )}

              {showNetAssistant && <NetAssistant adId={ad.id} onOpenNet={onOpenNet} pageName={pageName} />}

              <Trust
                ad={ad}
                adType={adType}
                onOpenDrawer={setActiveDrawer}
                vehicleCertification={vehicleCertification}
                vehicleHistory={vehicleHistory}
              />

              <Advantages ad={ad} adType={adType} vehicleHistory={vehicleHistory} />

              <FinancingPanel
                ad={ad}
                financingData={financingData}
                onContactSellerCtaClick={handleFinancingContactSellerCtaClick}
                onFinancingDataChange={setAppliedFinancingData}
                onOpenDrawer={setActiveDrawer}
                seller={seller}
                showB2cFinancingPanel={showB2cFinancingPanel}
                showC2cFinancing={showC2cFinancing}
                vehicleInfo={vehicleInfo}
              />

              <Seller
                ad={ad}
                isDealerFavorite={isDealerFavorite}
                onOpenDrawer={setActiveDrawer}
                pageName={pageName}
                seller={seller}
                dealerAds={dealerAds}
                dealerReviews={dealerReviews}
                trackingData={extendedTrackingData}
              />

              <RelatedAds relatedAds={relatedAds} />

              <News newsFeed={newsFeedAdapted} />

              <ReportAd ad={ad} onOpenDrawer={setActiveDrawer} seller={seller} />

              <FinancingConditions financingData={financingData} seller={seller} showC2cFinancing={showC2cFinancing} />
            </div>
          </div>

          <NavigationFooter
            ad={ad}
            breadcrumb={breadcrumb}
            tagclouds={tagclouds}
            pageErrors={pageErrors}
            pageName={pageName}
          />
        </main>

        {/*
          The bar derives aria-haspopup and aria-expanded from the PRESENCE of
          onOpenDrawer, and falls back to scrolling to the seller section without it —
          the only behaviour the INLINE case can honestly offer, since a private
          seller's form renders no FormDrawer for `activeDrawer` to open.

          So useContactCardProps spreads the drawer props in, never sets them to
          undefined. This bar is a direct child of a PrimitiveInjector layout, which
          clones its direct children and combines every prop matching /^on[A-Z]/ into a
          composed handler. A key that exists even with an undefined value means a
          conditional-undefined `onOpenDrawer` reaches the bar as a truthy function
          that composes zero handlers: on a private seller the bar would claim
          aria-haspopup="dialog", take the drawer branch on click, and return before
          the scroll fallback — a CTA that is dead and lies to assistive tech.
          Omitting the key is what makes the absence real.
        */}
        <StickyContactBar {...stickyContactBarProps} />
        <DetailDrawer activeDrawer={activeDrawer} onClose={onCloseDrawer} />

        {/*
          Only after an auto-registration, and it takes precedence over the post-contact
          modal below — legacy gates that one on `!showChangePasswordModal`
          (DetailFormContactSellerModalWrapper.js:141) so the two can never stack. The
          post-contact modal is not cancelled, only deferred: its timer still runs and
          it renders once this one is dismissed.
        */}
        {autoGeneratedPassword && (
          <Suspense fallback={null}>
            <LazyModalChangeUserPassword
              currentPassword={autoGeneratedPassword}
              isOpen
              onClose={clearAutoGeneratedPassword}
              onSuccess={clearAutoGeneratedPassword}
            />
          </Suspense>
        )}

        {/*
          Layers OVER the success drawer, which stays mounted and visible behind it.
          No Figma node shows this pairing, so there is no z-index engineering here
          by design — it is a manual check.
        */}
        {!autoGeneratedPassword && openModal === POST_CONTACT_MODALS.LAST_CALL && (
          <Suspense fallback={null}>
            <LazyModalLastCall
              adId={ad.id}
              adTitle={ad.title}
              dealerId={seller.dealer?.id}
              makeId={ad.makeId}
              // Make AND model, despite the prop name: the shipped copy reads
              // "Vemos tu interés en el KIA Niro nuevo". Reducing it to `make`
              // alone silently degrades every Last Call.
              makeName={[ad.make, ad.model].join(' ')}
              userData={{
                ...user,
                name: contactUserData?.name,
                zipCode: contactUserData?.zipCode,
                userId: contactUserData?.userId
              }}
              onClose={onClosePostContactModal}
              open
              vehicleId={vehicleInfo.uniqueVehicleId}
            />
          </Suspense>
        )}

        {/*
          The other half of legacy's either/or: shown when the ad is not Last Call
          eligible. Mounted only once open, like Last Call, so its impression event
          cannot fire on a detail view that never led to a lead.
        */}
        {!autoGeneratedPassword && openModal === POST_CONTACT_MODALS.RELATED_ADS && (
          <Suspense fallback={null}>
            <LazyModalRelatedAds
              ads={relatedAds}
              contactType={contactType}
              hasClickableCards
              hasCriteoEvents
              message={i18n.t('FORM.CONTACT_MESSAGE_PLACEHOLDER')}
              onContactAd={onContactRelatedAd}
              onModalClose={onClosePostContactModal}
              onToggleModal={onToggleRelatedAdsModal}
              showInitialNotification={contactType === listRelatedAdsContactTypes.email}
              showModal
              userData={{
                ...user,
                name: contactUserData?.name,
                email: contactUserData?.email,
                phone: contactUserData?.phone
              }}
            />
          </Suspense>
        )}

        {showNetAssistant && (
          <NetDrawer
            context={{page: {type: NET_SUGGESTION_TYPE.AD_DETAIL, adId: ad.id}}}
            headerText={i18n.t('NET.WELCOME.TEXT_AD_DETAIL')}
            initialSearchValue={netSearchValue}
            initialSuggestions={netInitialSuggestions}
            isOpen={isNetOpen}
            onClose={onCloseNet}
            onOpen={onOpenNet}
            pageName={pageName}
          />
        )}
        <Helmet {...helmet.head} bodyAttributes={{class: `${BASE_CLASS}-page`}} />
        {helmet.scripts?.map((structuredData, index) => {
          return <ScriptJsonLD json={structuredData} key={index} />
        })}
      </Layout.FullWidth>
      <ToastViewport>
        <ToastList />
      </ToastViewport>
    </>
  )
}

DetailNewContent.displayName = 'DetailNewContent'

// The content reads the toast context that useFavorites writes to, so the provider has to sit above it
const DetailNew = props => (
  <ToastProvider>
    <DetailNewContent {...props} />
  </ToastProvider>
)

DetailNew.displayName = 'DetailNew'

export default DetailNew
